/**
 * The landing page — what an anonymous visitor to svcdue.com sees.
 *
 * Until this existed, the whole product was a sign-in card: a stranger arriving
 * at the domain got a Google button and a one-line tagline, with no way to learn
 * what they would be signing in to. This page answers that before asking for an
 * account.
 *
 * Every claim on it is a feature that actually ships. The build plan describes a
 * v1 that is not all built yet — crew event-logging in particular has no screen
 * (README, "Known gaps") — so it is deliberately absent here. A landing page
 * that promises a screen the app does not have is a bug report waiting to
 * happen.
 *
 * A visitor who deep-links to a guarded route still gets the compact `SignIn`
 * card instead of this (RequireAuth): someone clicking through to /garage is
 * already sold and wants the door, not the pitch.
 */

import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock, Gauge, Link2, Lock, ShieldCheck, Users, Wrench } from 'lucide-react';

import { Tree } from '../components/primitives';
import { Footer } from '../components/Footer';
import { SignInAction } from '../auth/SignIn';

interface Feature {
  icon: JSX.Element;
  title: string;
  body: string;
}

/**
 * The six things worth knowing, in the order a racer would ask them: what does
 * it work out, what does it remind me about, what does it cover, who else gets
 * in, who can see it, and what happens to my data.
 */
const FEATURES: Feature[] = [
  {
    icon: <Gauge size={17} />,
    title: 'A forecast, not a checklist',
    body: 'Give a part its interval — passes, miles, runs or time — and SvcDue walks your calendar of upcoming events, landing each service where the interval actually crosses. Whichever comes first wins.',
  },
  {
    icon: <ShieldCheck size={17} />,
    title: 'Safety gear that expires',
    body: 'Belts, chutes, helmets and the rest come due on a date rather than on usage. SvcDue tracks those dates and flags them 60 and 30 days out — and emails the garage a weekly digest — so nothing gets discovered in the tech line.',
  },
  {
    icon: <CalendarClock size={17} />,
    title: 'The whole season on one timeline',
    body: 'Planned events and forecast service in one column, newest first. The next occurrence is firm; the ones after it are estimates, drawn dashed, because each assumes you serviced at the tile before it.',
  },
  {
    icon: <Users size={17} />,
    title: 'Your fleet, your crew',
    body: 'As many cars and drivers as you run, each with its own intervals. Invite crew to handle day-to-day maintenance while ownership of the fleet stays with you.',
  },
  {
    icon: <Link2 size={17} />,
    title: 'Share a build sheet by link',
    body: 'Flip on sharing and you get an unguessable link to a read-only page: the fleet, what is coming up, and the parts list if you want it. No member names, no history, no personal details.',
  },
  {
    icon: <Lock size={17} />,
    title: 'Private by default',
    body: 'Your garage is visible to you and the crew you invite, and to nobody else unless you deliberately share it. We store your name and email. No ads, no third-party trackers, no marketing email, ever.',
  },
];

export function Landing() {
  return (
    <div className="svcdue landing">
      <div className="landing-main">
        <header className="landing-hero">
          <div className="brand landing-brand">
            <span className="brand-mark">
              <span className="bm-b bm-a" />
              <span className="bm-b bm-a" />
              <span className="bm-b bm-g" />
            </span>
            <div className="brand-txt">
              <b>SvcDue</b>
              <span>race maintenance</span>
            </div>
          </div>

          <div className="landing-tree">
            <Tree animate />
          </div>

          <h1 className="h1 landing-h">Know what's due before you load the trailer.</h1>
          <p className="lede landing-lede">
            A maintenance tracker built for the way a race car actually wears out — by passes and miles, not by the
            calendar alone. Track wear parts, safety-gear expirations and inspections across your fleet, and see what
            comes due at which event.
          </p>

          {/* Said before the sign-in button, not in small print under it. */}
          <p className="landing-dev">
            <Wrench size={12} /> In active development — expect rough edges, and features still landing.
          </p>

          <SignInAction />
          <p className="landing-fine">Free to use. Sign in with Google — no separate password to forget.</p>
        </header>

        <section className="landing-features" aria-label="What SvcDue does">
          {FEATURES.map((f) => (
            <div className="landing-feature" key={f.title}>
              <span className="landing-feature-i">{f.icon}</span>
              <h2 className="landing-feature-h">{f.title}</h2>
              <p className="landing-feature-b">{f.body}</p>
            </div>
          ))}
        </section>

        {/* Deliberately not a seventh feature card: the calculator is not part of
            what an account gets you, it is the one thing here you can use
            without one. Giving it its own strip is what says so. */}
        <section className="landing-tool" aria-label="Free tools">
          <div>
            <div className="eyebrow">Free · no sign-in</div>
            <h2 className="landing-tool-h">Class calculator</h2>
            <p className="landing-tool-b">
              Enter a combination and see which classes fit it — NHRA and IHRA sportsman, the drag-and-drive fields at
              Hot Rod Drag Week and Sick Week, and the radial-tire classes — and the minimum safety equipment the
              rulebooks call for at that pace and on that fuel. If it needs a competition licence or a certified
              chassis, it explains what each of those actually takes. It runs entirely in your browser: nothing is sent
              anywhere, nothing is stored.
            </p>
          </div>
          <Link to="/tools/classcalc" className="landing-tool-cta">
            Open the calculator <ArrowRight size={14} />
          </Link>
        </section>

        <section className="landing-how" aria-label="How it works">
          <div className="eyebrow">How it works</div>
          <ol className="landing-steps">
            <li>
              <b>Add a car and its parts.</b> Each part gets the interval you actually run it to — every 20 passes,
              every 3,000 miles, once a season, or a fixed expiration date.
            </li>
            <li>
              <b>Put your events on the calendar.</b> Estimate the passes and miles each weekend will add. That
              estimate is what the forecast runs on.
            </li>
            <li>
              <b>Log service as you do it.</b> The interval resets from that point and the timeline redraws itself.
            </li>
          </ol>
        </section>

        {/* Straight from the Terms. Better said here, before an account exists. */}
        <p className="landing-caveat">
          SvcDue is a convenience tool — it is not a substitute for your rulebook, your tech inspector, or your own
          judgment.
        </p>
      </div>

      <Footer />
    </div>
  );
}
